//
//      router.js
//      Stage Vault
//
//      Created by Claude on 2026-06-30.
//      Tiny URL-driven router. Paths drive the main view (vault, show
//      details, player). The hash drives which modal is open. The router is
//      the single source of truth — render functions are only called from
//      applyRoute(), and every navigation goes through navigate() /
//      openModalRoute() / closeModalRoute() so the URL and the visible UI
//      stay in lock-step (browser back/forward included).
//

import $ from 'jquery';
import * as bootstrap from 'bootstrap';

import { renderLibrary } from '/js/library.js';
import { renderShowDetails } from '/js/details.js';
import { renderPlayer, teardownPlayer } from '/js/stream.js';
import { renderRedemptionModal } from '/js/redeem.js';
import { renderInvite } from '/js/invite.js';
import { showSettings } from '/js/accounts.js';
import { showSecurity } from '/js/navigation.js';

// Known hash → modal mapping. Each entry knows its DOM id so we can detect
// stale instances and tear them down deterministically.
const MODAL_HASHES = {
  redeem:   { id: 'redemptionModal', open: openRedeem },
  settings: { id: 'settingsModal',   open: showSettings },
  security: { id: 'securityModal',   open: showSecurity },
};

function openRedeem() {
  // renderRedemptionModal appends a brand-new node — clear any stale copy
  // first so we don't end up with two overlays in the DOM.
  $('#redemptionModal').remove();
  renderRedemptionModal();
  const el = document.getElementById('redemptionModal');
  if (!el) return;
  bootstrap.Modal.getOrCreateInstance(el).show();
}

// Most recently parsed main view, used so we don't re-render the same view
// when only the modal hash changed.
let currentView = null;

function parsePath() {
  const path = window.location.pathname;
  let m;
  if ((m = path.match(/^\/shows\/([^/]+)\/watch\/?$/))) {
    // ?start=0 (the details page's "Play from Beginning" button) makes the
    // player skip its auto-resume seek.
    const fromBeginning = new URLSearchParams(window.location.search).get('start') === '0';
    return { kind: 'watch', showId: decodeURIComponent(m[1]), fromBeginning };
  }
  if ((m = path.match(/^\/shows\/([^/]+)\/?$/))) {
    return { kind: 'details', showId: decodeURIComponent(m[1]) };
  }
  if ((m = path.match(/^\/invite\/([A-Za-z0-9]+)\/?$/))) {
    // Organization-invitation screen; the token comes from the email link.
    return { kind: 'invite', token: m[1] };
  }
  return { kind: 'library' };
}

function sameView(a, b) {
  if (!a || !b) return false;
  return a.kind === b.kind && a.showId === b.showId && a.token === b.token;
}

// Render the main view that matches the current path, then sync the modal
// state. Idempotent for the main view: re-running with no path change won't
// re-render the page, which keeps Bootstrap dropdowns, focus, and scroll
// position intact when the user only toggles a modal.
export function applyRoute() {
  const view = parsePath();

  if (!sameView(view, currentView)) {
    // Leaving the watch view — flush playback progress and detach the
    // player's window-level listeners before the DOM is replaced.
    if (currentView && currentView.kind === 'watch' && view.kind !== 'watch') {
      teardownPlayer();
    }
    currentView = view;
    if (view.kind === 'watch') {
      renderPlayer(view.showId, { fromBeginning: view.fromBeginning });
    } else if (view.kind === 'details') {
      renderShowDetails(view.showId);
    } else if (view.kind === 'invite') {
      renderInvite(view.token);
    } else {
      renderLibrary();
    }
  }

  applyModal();
}

// Forget the cached view so the next applyRoute() re-renders from scratch.
// Needed when a full-screen takeover (the sign-in prompt) replaces the DOM
// without going through the router — otherwise applyRoute() would think the
// old view is still on screen and skip rendering. Also flushes the player if
// the takeover happened mid-playback.
export function resetRouteCache() {
  if (currentView && currentView.kind === 'watch') {
    teardownPlayer();
  }
  currentView = null;
}

// Programmatic "go to the vault and show a banner" helper. Actions that
// change vault contents (redeeming a code, saving settings) can fire from any
// page — this keeps the URL, the router's view cache, and the DOM in sync
// while still letting callers pass a one-off alert through to renderLibrary().
export function renderLibraryRoute(alertClass = null, alertMessage = null) {
  if (currentView && currentView.kind === 'watch') {
    teardownPlayer();
  }
  const atLibrary = window.location.pathname === '/' && !window.location.hash;
  if (!atLibrary) {
    history.pushState({}, '', '/');
  }
  currentView = { kind: 'library' };
  renderLibrary(alertClass, alertMessage);
}

function applyModal() {
  const hash = (window.location.hash || '').replace(/^#/, '');

  // Hide any open known modal whose hash no longer matches the URL.
  Object.entries(MODAL_HASHES).forEach(([name, m]) => {
    if (name === hash) return;
    const el = document.getElementById(m.id);
    if (el && el.classList.contains('show')) {
      const inst = bootstrap.Modal.getInstance(el);
      if (inst) inst.hide();
    }
  });

  const target = MODAL_HASHES[hash];
  if (!target) return;

  const existing = document.getElementById(target.id);
  if (existing && existing.classList.contains('show')) {
    // Already open — nothing to do.
    return;
  }

  target.open();
}

// Document-level delegate that clears the URL hash whenever a known modal
// finishes hiding. Bound once at module load — we deliberately avoid jQuery
// sub-namespaces on `hidden.bs.modal` because Bootstrap doesn't emit them,
// and jQuery requires every namespace specified on the handler to be present
// on the event for the handler to fire. A delegate on document also keeps
// working when the modal element is removed by its own hidden handler
// (which the settings modal does on dismiss).
document.addEventListener('hidden.bs.modal', (event) => {
  const el = event.target;
  if (!el || !el.id) return;
  const entry = Object.entries(MODAL_HASHES).find(([, m]) => m.id === el.id);
  if (!entry) return;
  const [name] = entry;
  if (window.location.hash === '#' + name) {
    history.replaceState({}, '', window.location.pathname + window.location.search);
  }
}, true);

// Push a new path onto the history stack and re-evaluate the route. If the
// caller passes the URL we're already at, fall through to a no-op so the
// stack doesn't grow uselessly.
export function navigate(path, { replace = false } = {}) {
  const current = window.location.pathname + window.location.search + window.location.hash;
  if (current === path) return;
  if (replace) {
    history.replaceState({}, '', path);
  } else {
    history.pushState({}, '', path);
  }
  applyRoute();
}

// Add a modal hash on top of whatever path the user is currently on.
export function openModalRoute(name) {
  const target = window.location.pathname + window.location.search + '#' + name;
  navigate(target);
}

// Strip the current hash without touching the path.
export function closeModalRoute() {
  if (!window.location.hash) return;
  navigate(window.location.pathname + window.location.search);
}

// Browser back/forward — re-derive the visible state from the URL.
window.addEventListener('popstate', applyRoute);
